/**
 * Template Section Content
 *
 * Content renderer for template sections using EditorView for markdown rendering.
 * Always renders markdown (plain text is automatically converted to paragraphs).
 */

import React from 'react';
import { EditorView } from '@components/BlockNote/EditorView';
import { Box, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { TemplateSectionContentProps } from './types';

export const TemplateSectionContent: React.FC<TemplateSectionContentProps> = ({
  markdown,
  isLoading = false,
  loadingMessage = 'Generating preview...',
  collapsed = false,
  isExample = false,
  sx,
}) => {
  const theme = useTheme();

  const hasContent = markdown && markdown.trim().length > 0;

  return (
    <Collapse in={!collapsed}>
      {isLoading || !hasContent ? (
        <Box
          sx={{
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            color: theme.palette.text.secondary,
            ...sx,
          }}
        >
          <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {loadingMessage}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            pt: 0,
            pb: 0,
            px: 0,
            mt: '-1px',
            ...sx,
          }}
        >
          {isExample && (
            <Box
              sx={{
                px: 2,
                pt: 1.5,
                pb: 0.5,
                mx: '-1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Chip
                label="Example"
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  bgcolor: theme.palette.info.main + '15',
                  color: theme.palette.info.dark,
                  fontWeight: 500,
                }}
              />
            </Box>
          )}
          <EditorView
            markdown={markdown || ''}
            editable={false}
            sideMenu={false}
            formattingToolbar={false}
            sx={{
              mx: '-2rem',
            }}
          />
        </Box>
      )}
    </Collapse>
  );
};

